import { ApolloClient, ApolloError } from "@apollo/client";
import { CalendarDate, endOfMonth, startOfMonth } from "@internationalized/date";
import identity from "lodash/identity";
import noop from "lodash/noop";
import { assign, createMachine } from "xstate";
import { invariantEvent } from "xstate-helpers/events";
import { send } from "xstate/lib/actions";

import type { Dayjs } from "@holibob-packages/dayjs";

import { AvailabilityListItem, AvailabilityListResponse, AVAILABILITY_LIST_QUERY, Availability } from "../../apiHooks";
import { ConsumerApiError, ConsumerProductSoldOutError } from "../../custom-events";
import { findAmazonRequestIdForGraphQLError } from "../../utils/findAmazonRequestIdForGraphQLError";

type Event =
    | {
          type: "SET_CALENDAR_MONTH";
          calendarMonth: CalendarDate;
      }
    | {
          type: "AVAILABILITY_LIST_UPDATE";
          calendarMonth: CalendarDate;
          state: "pending" | "hasAvailability" | "noAvailability";
          availabilityList: AvailabilityListItem[];
      }
    | {
          type: "CHECK_FUTURE_AVAILABILITY";
          calendarMonth: CalendarDate;
      };

export type AvailabilityByMonth = {
    state: "pending" | "hasAvailability" | "noAvailability";
    availabilityList: AvailabilityListItem[];
};
type Context = {
    productId: string;
    timeZone: string | null;
    apolloClient: ApolloClient<unknown>;
    initialAvailability?: Availability | null;
    defaultAvailabilityListItem: AvailabilityListItem | null;
    calendarMonth: CalendarDate;
    validDates: string[];
    availabilityListByMonth: Record<string, AvailabilityByMonth | undefined>;
    warningMessages: {
        dateIsInNewMonth: boolean;
    };
};

export type State = "searchingForAvailability" | "foundAvailability" | "noAvailability";

export const availabilityDatePickerMachine = createMachine(
    {
        context: {
            productId: null!,
            timeZone: null,
            apolloClient: null!,
            calendarMonth: calendarDate({ year: new Date().getFullYear(), month: new Date().getMonth() + 1 }),
            initialAvailability: null,
            defaultAvailabilityListItem: null,
            availabilityListByMonth: {},
            validDates: [],
            warningMessages: {
                dateIsInNewMonth: false,
            },
        },
        tsTypes: {} as import("./AvailabilityDatePicker.machine.typegen").Typegen0,
        schema: { context: {} as Context, events: {} as Event },
        id: "availabilityDatePickerMachine",
        invoke: {
            id: "future-checker",
            src: (ctx) => (send, onReceive) => {
                onReceive((event: Event) => {
                    invariantEvent(event, "CHECK_FUTURE_AVAILABILITY");
                    const calendarMonth = event.calendarMonth;

                    void checkDate(calendarMonth);
                    void checkDate(calendarMonth.add({ months: 1 }));
                    void checkDate(calendarMonth.add({ months: 2 }));
                    void checkDate(calendarMonth.add({ months: 3 }));
                });

                async function checkDate(calendarMonth: CalendarDate) {
                    send({
                        type: "AVAILABILITY_LIST_UPDATE",
                        calendarMonth: calendarMonth,
                        state: "pending",
                        availabilityList: [],
                    });

                    try {
                        const response = await ctx.apolloClient.query<AvailabilityListResponse>({
                            query: AVAILABILITY_LIST_QUERY,
                            variables: {
                                productId: ctx.productId,
                                startDate: startOfMonth(calendarMonth).toString(),
                                endDate: endOfMonth(calendarMonth).toString(),
                            },
                            fetchPolicy: "no-cache",
                        });
                        send({
                            type: "AVAILABILITY_LIST_UPDATE",
                            calendarMonth: calendarMonth,
                            availabilityList: response.data.availabilityList.nodes,
                            state:
                                response.data.availabilityList.nodes.length > 0 ? "hasAvailability" : "noAvailability",
                        });
                    } catch (e) {
                        send({
                            type: "AVAILABILITY_LIST_UPDATE",
                            calendarMonth: calendarMonth,
                            state: "noAvailability",
                            availabilityList: [],
                        });

                        const requestId = e instanceof ApolloError ? findAmazonRequestIdForGraphQLError(e) : undefined;

                        document.body.dispatchEvent(
                            new ConsumerApiError({
                                code: "AVAILABILITY_LIST",
                                productId: ctx.productId,
                                startDate: startOfMonth(calendarMonth).toString(),
                                endDate: endOfMonth(calendarMonth).toString(),
                                errorMessage: e instanceof Error ? e.message : "UNKNOWN",
                                requestId,
                            })
                        );
                    }
                }
            },
        },
        initial: "searchingForAvailability",
        on: {
            SET_CALENDAR_MONTH: {
                actions: ["assignCalendarMonth", "checkFutureAvailability"],
            },
            AVAILABILITY_LIST_UPDATE: {
                actions: "assignAvailabilityListByMonth",
            },
        },
        states: {
            searchingForAvailability: {
                entry: "checkFutureAvailability",
                always: [
                    {
                        cond: "noAvailabilityForAYear",
                        target: "noAvailabilityInFirstYear",
                        actions: ["notifyNoAvailabilityInFirstYear"],
                    },
                    {
                        cond: "foundAvailability",
                        target: "foundAvailability",
                        actions: ["calculateDefaultAvailabilityListItem", "notifyDefaultAvailabilityItemFound"],
                    },
                    {
                        actions: ["moveCalendarDateForward", "setWarningDateIsInNewMonth", "checkFutureAvailability"],
                        cond: "noAvailabilityForCurrentMonth",
                    },
                ],
            },
            foundAvailability: {},
            noAvailabilityInFirstYear: {},
        },
    },
    {
        actions: {
            notifyNoAvailabilityInFirstYear: noop,
            notifyDefaultAvailabilityItemFound: noop,
            checkFutureAvailability: send(
                (ctx) => ({ type: "CHECK_FUTURE_AVAILABILITY", calendarMonth: ctx.calendarMonth }),
                { to: "future-checker" }
            ),
            moveCalendarDateForward: assign({
                calendarMonth: (ctx) => ctx.calendarMonth.add({ months: 1 }),
            }),
            setWarningDateIsInNewMonth: assign({
                warningMessages: (ctx) => ({
                    ...ctx.warningMessages,
                    dateIsInNewMonth: true,
                }),
            }),
            assignAvailabilityListByMonth: assign({
                availabilityListByMonth: ({ availabilityListByMonth }, e) => {
                    const month = toMonthString(e.calendarMonth);
                    const existingAvailabilityList = availabilityListByMonth[month];

                    if (existingAvailabilityList && existingAvailabilityList.state !== "pending") {
                        // do not overwrite existing availability list if has resolved once
                        return availabilityListByMonth;
                    }

                    return {
                        ...availabilityListByMonth,
                        [month]: identity<AvailabilityByMonth>({
                            state: e.state,
                            availabilityList: e.availabilityList,
                        }),
                    };
                },
                validDates: ({ validDates }, e) => {
                    return [
                        // get unique valid dates (just in case, usually they will be valid by default)
                        ...new Set([
                            ...validDates,
                            ...e.availabilityList.map((availabilityListItem) => availabilityListItem.date),
                        ]),
                    ];
                },
            }),
            calculateDefaultAvailabilityListItem: assign({
                defaultAvailabilityListItem: (ctx) => {
                    const { initialAvailability } = ctx;
                    const month = toMonthString(ctx.calendarMonth);
                    const availabilityByMonth = ctx.availabilityListByMonth[month];

                    if (availabilityByMonth?.state === "hasAvailability") {
                        if (!availabilityByMonth.availabilityList.length) {
                            throw new Error(
                                'invariant exception: Expected availabilityByMonth with state "hasAvailability" to have non-empty availabilityList'
                            );
                        }
                        const sortedAvailabilities = availabilityByMonth.availabilityList
                            .slice()
                            .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
                        let defaultAvailability = sortedAvailabilities[0];

                        if (initialAvailability && sortedAvailabilities.length > 0) {
                            const initialAvailabilityDate = initialAvailability.date;
                            const initialAvailabilityMatchByDate = sortedAvailabilities.find(
                                (availability) => availability.date === initialAvailabilityDate
                            );
                            if (initialAvailabilityMatchByDate) {
                                defaultAvailability = initialAvailabilityMatchByDate;
                            }
                        }

                        return defaultAvailability;
                    } else {
                        console.warn("No availability found for current month");
                        return initialAvailability
                            ? {
                                  id: initialAvailability.id,
                                  date: initialAvailability.date,
                                  soldOut: initialAvailability.soldOut,
                              }
                            : ctx.defaultAvailabilityListItem;
                    }
                },
            }),
            assignCalendarMonth: assign({
                calendarMonth: (_, e) => e.calendarMonth,
            }),
        },
        guards: {
            noAvailabilityForCurrentMonth: ({ calendarMonth, availabilityListByMonth }) => {
                const month = toMonthString(calendarMonth);
                return availabilityListByMonth[month]?.state === "noAvailability";
            },
            noAvailabilityForAYear: ({ calendarMonth, availabilityListByMonth, productId }) => {
                const months = Object.keys(availabilityListByMonth);
                if (months.length < 12) return false;

                const hasNoAvailabilityForAYear = months
                    .slice(0, 12)
                    .every((month) => availabilityListByMonth[month]?.state === "noAvailability");

                if (!hasNoAvailabilityForAYear) {
                    return false;
                }

                const startDate = startOfMonth(calendarMonth).toString();
                const endDate = endOfMonth(calendarMonth.add({ months: 11 })).toString();

                document.body.dispatchEvent(
                    new ConsumerProductSoldOutError({
                        productId: productId,
                        startDate,
                        endDate,
                    })
                );

                return true;
            },
            foundAvailability: ({ calendarMonth, availabilityListByMonth }) => {
                const month = toMonthString(calendarMonth);
                return availabilityListByMonth[month]?.state === "hasAvailability";
            },
        },
    }
);

export function toMonthString(date: CalendarDate) {
    return `${date.year}-${date.month.toString().padStart(2, "0")}`;
}
export function toDateString(date: CalendarDate | Dayjs) {
    return date instanceof CalendarDate ? date.toString() : date.format("YYYY-MM-DD");
}
export function calendarDate(calendarMonth: { year: number; month: number }) {
    return new CalendarDate(calendarMonth.year, calendarMonth.month, 1);
}
